import {
    IComponentConfig,
    IComponentDefinitionConfig,
    IComponentInstanceConfig,
    IDedicatedSkinConfig,
    IPropConfig,
    ISkinConfig,
    IUserFlow,
    PropConfigBehaviorTypes,
    v2_IVariantConfig
} from "../common";

const convertV2VariantToComponentConfig = (config: v2_IVariantConfig): IComponentConfig => {
    return {
        instance: {
            id: 'auto',
            componentId: config.componentId,
            componentName: config.name,

            skinSelector: {
                type: 'Dedicated',
                skin: {
                    id: 'skin_auto_v2_converted'
                }
            } as IDedicatedSkinConfig

        } as IComponentInstanceConfig,
        type: {
            id: config.id,
            name: config.name,
            description: config.description,
            props: [
                {
                    id: 'in_auto_v2_converted',
                    name: 'myop_init_interface',
                    type: 'any',
                    behavior: {
                        type: PropConfigBehaviorTypes.code
                    }
                } as IPropConfig,

                {
                    id: 'out_auto_v2_converted',
                    name: 'myop_cta_handler',
                    type: 'any',
                    behavior: {
                        type: PropConfigBehaviorTypes.code
                    }
                } as IPropConfig
            ],
            refs: [],
            skins: [
                {
                    id: 'skin_auto_v2_converted',
                    name: 'auto_v2_converted',
                    description: '',
                    //TODO : handle empty :
                    // @ts-ignore
                    loader: config.consume_variant[0].loader
                } as ISkinConfig
            ],
            defaultSkin: 0
        } as IComponentDefinitionConfig,
        name: config.name
    } as IComponentConfig;
};

/**
 * CloudRepository - Unified cloud data access layer for Myop components
 *
 * Supports both v2 (default) and v1 modes:
 * - v2: Uses /consume endpoint for fetching variants
 * - v1: Uses /flow endpoint for fetching user flows
 */
// Use a global singleton to ensure only one CloudRepository.Main exists
// even when the module is loaded multiple times by different bundlers
const GLOBAL_KEY = '__MYOP_CLOUD_REPOSITORY_MAIN__';

const getGlobalMain = (): CloudRepository | undefined => {
    if (typeof window !== 'undefined') {
        return (window as any)[GLOBAL_KEY];
    }
    if (typeof globalThis !== 'undefined') {
        return (globalThis as any)[GLOBAL_KEY];
    }
    return undefined;
};

const setGlobalMain = (instance: CloudRepository) => {
    if (typeof window !== 'undefined') {
        (window as any)[GLOBAL_KEY] = instance;
    }
    if (typeof globalThis !== 'undefined') {
        (globalThis as any)[GLOBAL_KEY] = instance;
    }
};

export class CloudRepository {
    private static _main: CloudRepository | null = null;

    static get Main(): CloudRepository {
        // Check if a global instance already exists (from another bundle)
        const globalMain = getGlobalMain();
        if (globalMain) {
            return globalMain;
        }

        // Create new instance if needed
        if (!CloudRepository._main) {
            CloudRepository._main = new CloudRepository();
            setGlobalMain(CloudRepository._main);
        }
        return CloudRepository._main;
    }

    // v2 cache - keyed by componentId:env:preview|live
    // Each Promise is tagged with a `resolved` property after settling (see fetchComponentV2).
    private variants: Record<string, Promise<IComponentConfig> & { resolved?: IComponentConfig }> = {};

    // Track which components were preloaded and with what params
    // Key: componentId, Value: { env, preview }
    private preloadedComponents: Record<string, { env: string; preview: boolean }> = {};

    // v1 cache
    private userFlows: Record<string, Promise<IUserFlow>> = {};

    private _defaultEnv = 'production';
    private _envExplicitlySet = false;
    private _staticMode = false;
    private _queryParams: Record<string, string> = {};

    constructor(private _baseUrl = 'https://cloud.myop.dev') {
    }

    /**
     * Enable static mode for self-hosted component registries.
     * In static mode, URLs are constructed as file paths instead of query params:
     *   ${baseUrl}/components/${componentId}/${env}.json
     */
    enableStaticMode() {
        this._staticMode = true;
    }

    /**
     * Set query parameters to append to all fetch URLs (e.g. for CDN cache busting).
     * Example: `setQueryParams({ lastupdate: '11112026' })`
     */
    setQueryParams(params: Record<string, string>) {
        this._queryParams = params;
    }

    /**
     * Set the default environment for this CloudRepository instance
     */
    setEnvironment(env: string) {
        this._defaultEnv = env;
        this._envExplicitlySet = true;
    }

    /**
     * Get the current default environment
     */
    getDefaultEnvironment(): string {
        return this._defaultEnv;
    }

    // ============ V2 Methods (Default) ============

    /**
     * Check if a component is already cached/preloaded (v2)
     * If env and preview are not provided, checks if ANY version is preloaded
     */
    isPreloaded(componentId: string, env?: string, preview?: boolean): boolean {
        // If specific env/preview requested, check exact match
        if (env !== undefined || preview !== undefined) {
            const cacheKey = `${componentId}:${env || this._defaultEnv}:${preview ? 'preview' : 'live'}`;
            return cacheKey in this.variants;
        }
        // Otherwise check if any version was preloaded
        return componentId in this.preloadedComponents;
    }

    /**
     * Get the preloaded params for a component (if preloaded)
     * Returns undefined if not preloaded
     */
    getPreloadedParams(componentId: string): { env: string; preview: boolean } | undefined {
        return this.preloadedComponents[componentId];
    }

    /**
     * Fetch a v2 component config
     * If env/preview not specified and component was preloaded, uses preloaded params
     */
    async fetchComponentV2(componentId: string, environmentIdentifier?: string, preview?: boolean): Promise<IComponentConfig> {
        // If no explicit env/preview and component was preloaded, use preloaded params
        const preloaded = this.preloadedComponents[componentId];
        const hasExplicitParams = environmentIdentifier !== undefined || preview !== undefined;

        let env: string;
        let usePreview: boolean;

        if (!hasExplicitParams && preloaded) {
            // Use preloaded params
            env = preloaded.env;
            usePreview = preloaded.preview;
        } else {
            // Use provided params or defaults
            env = environmentIdentifier || this._defaultEnv;
            // If preview explicitly true → true
            // If preview explicitly false → false
            // If preview undefined: env was specified (via param or setEnvironment) → false, otherwise → true
            const hasEffectiveEnv = environmentIdentifier || this._envExplicitlySet;
            usePreview = preview === true ? true : (preview === false ? false : !hasEffectiveEnv);
        }

        const cacheKey = `${componentId}:${env}:${usePreview ? 'preview' : 'live'}`;

        console.log('[CloudRepository] fetchComponentV2', {
            componentId: componentId.slice(0, 8) + '...',
            env,
            usePreview,
            cacheKey,
            cached: cacheKey in this.variants
        });

        if (!this.variants[cacheKey]) {
            // Track this as preloaded (first fetch counts as preload)
            if (!this.preloadedComponents[componentId]) {
                this.preloadedComponents[componentId] = { env, preview: usePreview };
            }

            // eslint-disable-next-line no-async-promise-executor
            this.variants[cacheKey] = new Promise(
                async (resolve, reject) => {
                    try {
                        let url: string;
                        if (this._staticMode) {
                            const file = usePreview ? 'preview' : env;
                            url = `${this._baseUrl}/components/${componentId}/${file}.json`;
                            const qs = new URLSearchParams(this._queryParams).toString();
                            if (qs) url += `?${qs}`;
                        } else {
                            url = `${this._baseUrl}/consume?id=${componentId}&env=${env}`;
                            if (usePreview) {
                                url += '&preview=true';
                            }
                        }
                        const res = await fetch(url);
                        if (!res.ok) {
                            reject(new Error(`Failed to load component "${componentId}" (${res.status}${this._staticMode ? ` from ${url}` : ''})`));
                            return;
                        }
                        const json = await res.json();
                        const variant = json.item;

                        if (!variant) {
                            reject(new Error(`Component "${componentId}" not found`));
                            return;
                        }

                        if (!variant.consume_variant || !variant.consume_variant.length) {
                            reject(new Error(`Component "${componentId}" has no implementation for environment "${env}"`));
                            return;
                        }

                        const componentConfig = convertV2VariantToComponentConfig(variant);
                        // Tag the Promise so getResolvedConfig can read it synchronously
                        (this.variants[cacheKey] as any).resolved = componentConfig;
                        resolve(componentConfig);
                    } catch (e) {
                        // Clear the cached rejected promise so the next call
                        // makes a fresh request (e.g. after network recovery)
                        delete this.variants[cacheKey];
                        reject(e);
                    }
                }
            );
        }

        return await this.variants[cacheKey];
    }

    /**
     * Synchronous version of fetchComponentV2 — returns the config only if
     * the fetch already completed, null otherwise.
     *
     * Uses the same `this.variants` cache that fetchComponentV2/preloadComponents
     * populate. Each cached Promise is tagged with a `.resolved` property when
     * it settles — this method just reads that property. No separate cache.
     *
     * Why this exists: React Native needs the config at render time to embed it
     * in the WebView HTML (can't await in useState). If preloadComponents ran
     * earlier and the fetch completed, this returns the result synchronously,
     * skipping the WEBVIEW_READY → fetch → inject round trip entirely.
     */
    getResolvedConfig(componentId: string, env?: string, preview?: boolean): IComponentConfig | null {
        const preloaded = this.preloadedComponents[componentId];
        const hasExplicitParams = env !== undefined || preview !== undefined;

        let resolvedEnv: string;
        let usePreview: boolean;

        if (!hasExplicitParams && preloaded) {
            resolvedEnv = preloaded.env;
            usePreview = preloaded.preview;
        } else {
            resolvedEnv = env || this._defaultEnv;
            const hasEffectiveEnv = env || this._envExplicitlySet;
            usePreview = preview === true ? true : (preview === false ? false : !hasEffectiveEnv);
        }

        const cacheKey = `${componentId}:${resolvedEnv}:${usePreview ? 'preview' : 'live'}`;
        return this.variants[cacheKey]?.resolved || null;
    }

    // ============ V1 Methods (Legacy) ============

    /**
     * Fetch a v1 component from a flow
     */
    async fetchComponentV1(componentId: string, flowId?: string) {
        if (flowId) {
            const flow = await this.fetchFlow(flowId);
            return flow.components.find(c => c.type.id === componentId);
        } else {
            const flow = await this.fetchAutoFlow(componentId);
            return flow.components[0];
        }
    }

    async fetchAutoFlow(componentId: string) {
        if (!this.userFlows[componentId]) {
            // eslint-disable-next-line no-async-promise-executor
            this.userFlows[componentId] = new Promise(
                async (resolve, reject) => {
                    try {
                        const res = await fetch(
                            `${this._baseUrl}/flow?id=${componentId}&auto=true`
                        );
                        const json = await res.json();
                        resolve(json.item);
                    } catch (e) {
                        delete this.userFlows[componentId];
                        reject(e);
                    }
                }
            );
        }

        return await this.userFlows[componentId];
    }

    async fetchFlow(flowId: string) {
        if (!this.userFlows[flowId]) {
            // eslint-disable-next-line no-async-promise-executor
            this.userFlows[flowId] = new Promise(
                async (resolve, reject) => {
                    try {
                        const res = await fetch(
                            `${this._baseUrl}/flow?id=${flowId}&resolve=components`
                        );
                        const json = await res.json();
                        resolve(json.item);
                    } catch (e) {
                        delete this.userFlows[flowId];
                        reject(e);
                    }
                }
            );
        }

        return await this.userFlows[flowId];
    }

    // ============ Backward Compatibility ============

    /**
     * @deprecated Use fetchComponentV2 or fetchComponentV1 explicitly
     * Defaults to v1 behavior for backward compatibility with existing code
     */
    async fetchComponent(componentId: string, flowId?: string) {
        return this.fetchComponentV1(componentId, flowId);
    }
}

/**
 * @deprecated Use CloudRepository instead
 */
export const v2_CloudRepository = CloudRepository;
